import { useNavigate } from 'react-router-dom'

import { Button } from '@factorialco/deprecated-design-system'
import {
  PeopleChartStructureDocument,
  RemoveTeamMutationDocument,
  TeamDetailFragment,
  TeamsTeamListPageDocument,
} from 'generated/resources/graphql'

import Modal from 'modules/core/components/Modal'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import asyncAction from 'modules/core/lib/asyncAction'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { useUpsertHierarchy } from 'modules/people_chart/components/GroupChart/useUpsertHierarchy'
import hierarchy from 'modules/people_chart/lib/Hierarchy'
import hierarchyLib from 'modules/people_chart/lib/Hierarchy'

interface Props {
  team: TeamDetailFragment
  onClose: () => void
}

const RemoveTeamModal = ({ team, onClose }: Props) => {
  const { company } = useCurrentSession()
  const { data: structureData } = useQuery(PeopleChartStructureDocument)
  const [removeTeam] = useMutation(RemoveTeamMutationDocument, {
    extractorFn: (data) => data.teamsMutations.deleteTeamsTeam,
    refetchQueries: [TeamsTeamListPageDocument],
    awaitRefetchQueries: true,
  })
  const { upsert: upsertHierarchy } = useUpsertHierarchy()
  const companyId = company.id
  const hierarchyData = useUnsafeMemo(() => {
    return hierarchy.fromJSON(structureData?.peopleChart.structuresConnection.nodes[0]?.hierarchy)
  }, [structureData])
  const structureId = String(structureData?.peopleChart.structuresConnection.nodes[0]?.id)

  const navigate = useNavigate()
  const { navigation } = useNavigation()
  const name = team.name

  const upsert = async (hierarchyJson: string) => {
    const result = await upsertHierarchy({
      variables: {
        hierarchy: hierarchyJson,
        companyId: companyId,
        id: structureId,
      },
      awaitRefetchQueries: true,
      refetchQueries: [
        {
          query: PeopleChartStructureDocument,
        },
      ],
    })

    const handled = handleResult(result, () => undefined)

    if (!handled.success) {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('people_chart.employees_selector.error_modifying_chart'),
      })
    }
  }

  const onSubmit = async () => {
    const { success, value } = await asyncAction(async () => {
      await removeTeam({ variables: { id: team.id } })
    })

    track(trackingEvents.teams.deleteTeamSubmit, {
      is_successful: success,
      error_code: !success && JSON.stringify(value),
    })

    if (success) {
      const result = hierarchyLib.removeNodeAndLinkedNodes(hierarchyData || [], team.id)
      upsert(hierarchyLib.toJSON(result))

      onClose()
      navigate(navigation.employees.teams.fullPath())
    }
  }

  const handleClose = () => {
    onClose()
    track(trackingEvents.teams.deleteTeamCancel)
  }

  return (
    <Modal
      illustration="input-field"
      title={i18n.t('teams.confirm_delete.title', {
        name,
      })}
      description={i18n.t('teams.confirm_delete.description')}
      onClose={handleClose}
      onSubmit={onSubmit}
      footer={
        <Button type="negative" submit>
          {i18n.t('teams.delete')}
        </Button>
      }
    />
  )
}

export default RemoveTeamModal
